import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "./AuthContext";
import SessionExpiryModal from "./SessionExpiryModal";
import {
  SessionEventsContext,
  type ExpiryReason,
  type SessionEvents,
} from "./useSessionEvents";
import { useIdleTimer, type IdleStatus } from "./useIdleTimer";

/**
 * App-root session guard. Mounts once inside `<AuthProvider>` and:
 *
 *   1. Watches user activity via `useIdleTimer`.
 *   2. Opens a warning modal at 14 min idle (60s before the 15-min limit).
 *   3. Transitions to "expired" mode at the limit (or when something
 *      external — Apollo's `onAuthFailure` — calls
 *      `triggerExpiryWarning("auth-failure")` via the context).
 *   4. Offers an inline re-auth flow that keeps the user on the same
 *      route. Only when re-auth fails OR the user clicks "Sign out"
 *      does the redirect to `/login?reason=expired` happen.
 *
 * The `<SessionEventsContext>` provider is on the outside so any
 * descendant — including descendants of `<MemberProfile>` rendered by
 * the astrana-ui library — can call `triggerExpiryWarning` without
 * prop-drilling.
 */

const IDLE_LIMIT_MS = 15 * 60_000;
const WARNING_LEAD_MS = 60_000;

type GuardMode = "hidden" | "warning" | "expired";

export default function SessionGuard({ children }: { children: ReactNode }) {
  const auth = useAuthContext();
  const navigate = useNavigate();
  const [mode, setMode] = useState<GuardMode>("hidden");

  // The ref lets the same `triggerExpiryWarning` callback identity stay
  // stable across renders — important because `MemberProfilePage` passes
  // it to <GraphQLProvider onAuthFailure={...}>, which would otherwise
  // tear down its Apollo client every time the modal mode flips.
  const triggerRef = useRef<(reason: ExpiryReason) => void>(() => undefined);

  const idle = useIdleTimer({
    idleLimitMs: IDLE_LIMIT_MS,
    warningLeadMs: WARNING_LEAD_MS,
    enabled: auth.isAuthenticated,
  });

  // React to idle transitions. The mode-flip rules:
  //   - active   → hidden   (only if we were warning; expired stays sticky)
  //   - warning  → warning  (open the modal)
  //   - expired  → expired  (open the modal in expired UI)
  useEffect(() => {
    setMode((prev) => deriveMode(prev, idle.status, auth.isAuthenticated));
  }, [idle.status, auth.isAuthenticated]);

  // When transitioning into expired mode, end the session locally so the
  // user's tokens don't sit around. Inline re-auth will fetch fresh ones.
  // We do this *after* the mode flip so the modal stays mounted.
  const prevModeRef = useRef<GuardMode>("hidden");
  useEffect(() => {
    if (prevModeRef.current !== "expired" && mode === "expired") {
      void auth.logout();
    }
    prevModeRef.current = mode;
  }, [mode, auth]);

  triggerRef.current = (reason: ExpiryReason) => {
    if (!auth.isAuthenticated && reason === "auth-failure") {
      // Already signed out somehow — just let LoginPage handle it.
      return;
    }
    setMode("expired");
  };

  const sessionEvents = useMemo<SessionEvents>(
    () => ({
      // Stable identity across renders — read through the ref.
      triggerExpiryWarning: (reason) => triggerRef.current(reason),
    }),
    [],
  );

  // ───────── modal handlers ─────────

  const handleStaySignedIn = useCallback(async () => {
    // Reset the idle clock optimistically so the modal closes promptly.
    idle.reset();
    setMode("hidden");
    // Fire-and-forget: if the silent refresh fails, the next GraphQL
    // call will surface it via Apollo's onAuthFailure → expired modal.
    // Doing it here proves the refresh path works *before* the user
    // triggers a real query.
    void verifySessionAlive(auth);
  }, [idle, auth]);

  const handleSignBackInEntra = useCallback(async () => {
    await auth.loginWithEntra();
    setMode("hidden");
    idle.reset();
  }, [auth, idle]);

  const handleSignBackInB2c = useCallback(
    async (email: string, password: string) => {
      const provider = auth.selectedProvider;
      if (provider !== "pathways-b2c" && provider !== "provider-portal-b2c") {
        throw new Error("B2C re-auth attempted but no B2C provider selected.");
      }
      await auth.loginWithB2c(provider, email, password);
      setMode("hidden");
      idle.reset();
    },
    [auth, idle],
  );

  const handleSignOut = useCallback(() => {
    setMode("hidden");
    void auth.logout();
    navigate("/login?reason=expired", { replace: true });
  }, [auth, navigate]);

  const remainingMs = Math.max(0, IDLE_LIMIT_MS - idle.idleMs);

  return (
    <SessionEventsContext.Provider value={sessionEvents}>
      {children}
      {mode !== "hidden" && (
        <SessionExpiryModal
          mode={mode}
          remainingMs={remainingMs}
          selectedProvider={auth.selectedProvider}
          accountLabel={auth.accountLabel}
          onStaySignedIn={handleStaySignedIn}
          onSignBackInEntra={handleSignBackInEntra}
          onSignBackInB2c={handleSignBackInB2c}
          onSignOut={handleSignOut}
        />
      )}
    </SessionEventsContext.Provider>
  );
}

function deriveMode(
  prev: GuardMode,
  idleStatus: IdleStatus,
  isAuthenticated: boolean,
): GuardMode {
  if (!isAuthenticated) {
    // Once the user is fully signed out (e.g., after the redirect to
    // /login completes), drop the modal entirely.
    return "hidden";
  }
  if (idleStatus === "expired") return "expired";
  if (prev === "expired") return "expired"; // sticky once we get there
  if (idleStatus === "warning") return "warning";
  return "hidden";
}

/**
 * Touch the auth provider's silent-refresh path so a dead refresh token
 * surfaces immediately. We don't await — if it fails, the next GraphQL
 * call will trip `onAuthFailure` → expired modal anyway. Calling it here
 * just makes the failure happen sooner than waiting for the next query.
 */
async function verifySessionAlive(auth: ReturnType<typeof useAuthContext>) {
  // `getToken()` returns the in-memory cached token. The
  // background-scheduled refresh in AuthContext runs independently; we
  // don't currently expose an explicit "refresh now" method. Reading the
  // token is enough as a smoke check — if the user re-engages the
  // warning modal after a true refresh failure, the next state read will
  // be `null` and Apollo's first request will trip onAuthFailure.
  return auth.getToken();
}
